'use client';

import {
  buildBatchLabels,
  type BuildLabelOptions,
  type QrLabelInput
} from './zpl-builder';
import {
  matchProfile,
  profileToOptions,
  type PrinterProfile
} from './printer-profile';
import { randomFraction } from '@/lib/random';

// Labelary public ZPL renderer — no auth required for normal call rates.
// Path format: /v1/printers/{dpmm}dpmm/labels/{widthIn}x{heightIn}/{index}/
// Body: raw ZPL. Response: image/png by default.
const LABELARY_BASE = 'https://api.labelary.com/v1/printers';
const MM_PER_INCH = 25.4;

// Zebra DPI presets map to Labelary's dpmm units exactly:
// 203 → 8 dpmm, 300 → 12 dpmm, 600 → 24 dpmm. Math.round handles all three
// without fudge.
function dpiToDpmm(dpi: number): number {
  return Math.max(1, Math.round(dpi / MM_PER_INCH));
}

export interface PreviewZplOptions extends BuildLabelOptions {
  /** Zero-based label index — Labelary renders one label at a time. Defaults to 0. */
  index?: number;
}

export type PreviewResult =
  { ok: true; url: string; revoke: () => void } | { ok: false; error: string };

/**
 * Render an arbitrary ZPL string into a PNG via Labelary's public API.
 * The returned `url` is a blob URL — call `revoke()` when done to free memory.
 */
export async function previewZpl(
  zpl: string,
  { widthMm, heightMm, dpi, index = 0 }: PreviewZplOptions
): Promise<PreviewResult> {
  const widthIn = (widthMm / MM_PER_INCH).toFixed(2);
  const heightIn = (heightMm / MM_PER_INCH).toFixed(2);
  const dpmm = dpiToDpmm(dpi);
  const url = `${LABELARY_BASE}/${dpmm}dpmm/labels/${widthIn}x${heightIn}/${index}/`;

  // Labelary's free tier rate-limits at ~5 req/s. Retry up to 4 times on 429
  // with exponential backoff + jitter so a transient throttle doesn't tank
  // the batch.
  const MAX_ATTEMPTS = 5;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'image/png',
          // Labelary rejects text/plain (which fetch sets by default for a
          // string body) with 415. The form-urlencoded content-type is
          // accepted with the raw ZPL as the entire body.
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: zpl
      });
      if (response.status === 429 && attempt < MAX_ATTEMPTS - 1) {
        const base = 400 * Math.pow(2, attempt); // 400, 800, 1600, 3200
        await new Promise((r) => setTimeout(r, base + randomFraction() * 300));
        continue;
      }
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        return {
          ok: false,
          error: `Labelary ${response.status}${text ? `: ${text}` : ''}`
        };
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return {
        ok: true,
        url: blobUrl,
        revoke: () => URL.revokeObjectURL(blobUrl)
      };
    } catch (err) {
      if (attempt < MAX_ATTEMPTS - 1) {
        const base = 400 * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, base + randomFraction() * 300));
        continue;
      }
      return {
        ok: false,
        error: err instanceof Error ? err.message : String(err)
      };
    }
  }
  return { ok: false, error: 'Labelary request failed after retries' };
}

export interface PreviewLabelsArgs {
  /** Printer name used to match against profiles, optional. */
  printerName?: string;
  labels: QrLabelInput[];
  profiles?: PrinterProfile[];
  fallbackOptions: BuildLabelOptions;
  /** Which label in the batch to render. Defaults to the first one. */
  index?: number;
}

export interface PreviewItem {
  index: number;
  url: string;
  revoke: () => void;
}

export type PreviewBatchResult =
  { ok: true; items: PreviewItem[] } | { ok: false; error: string };

/**
 * Mirror of `printLabels` that renders to Labelary instead of dispatching
 * to BrowserPrint. Same arguments, same profile-matching, so what you see
 * in the preview is what the printer would emit.
 */
export async function previewLabels(
  args: PreviewLabelsArgs
): Promise<PreviewResult> {
  if (args.labels.length === 0) {
    return { ok: false, error: 'No labels to preview' };
  }
  const profile = matchProfile(args.printerName, args.profiles ?? []);
  // The QR↔text gaps are a UX preference, not a printer characteristic —
  // they always ride along from fallbackOptions even when a profile
  // matches.
  const options: BuildLabelOptions = profile
    ? {
        ...profileToOptions(profile),
        qrHeaderGapMm: args.fallbackOptions.qrHeaderGapMm,
        qrFooterGapMm: args.fallbackOptions.qrFooterGapMm,
        qrHeaderFontMm: args.fallbackOptions.qrHeaderFontMm,
        qrFooterFontMm: args.fallbackOptions.qrFooterFontMm
      }
    : args.fallbackOptions;
  const zpl = buildBatchLabels(args.labels, options);
  return previewZpl(zpl, {
    widthMm: options.widthMm,
    heightMm: options.heightMm,
    dpi: options.dpi,
    index: args.index ?? 0
  });
}

// Labelary's free tier allows ~5 req/s. With concurrency=2 + retry-on-429
// backoff we stay well under the limit even on long batches. Higher
// concurrency triggered cascading 429s where retries also got rejected.
const PREVIEW_CONCURRENCY = 2;

/**
 * Render every label in the batch and return the full set of blob URLs.
 * Requests are processed in chunks to respect Labelary's rate limit. On
 * failure of any label, all successful blobs are revoked and the first
 * error message is returned.
 */
export async function previewLabelsAll(
  args: Omit<PreviewLabelsArgs, 'index'>
): Promise<PreviewBatchResult> {
  if (args.labels.length === 0) {
    return { ok: false, error: 'No labels to preview' };
  }
  const profile = matchProfile(args.printerName, args.profiles ?? []);
  // The QR↔text gaps are a UX preference, not a printer characteristic —
  // they always ride along from fallbackOptions even when a profile
  // matches.
  const options: BuildLabelOptions = profile
    ? {
        ...profileToOptions(profile),
        qrHeaderGapMm: args.fallbackOptions.qrHeaderGapMm,
        qrFooterGapMm: args.fallbackOptions.qrFooterGapMm,
        qrHeaderFontMm: args.fallbackOptions.qrHeaderFontMm,
        qrFooterFontMm: args.fallbackOptions.qrFooterFontMm
      }
    : args.fallbackOptions;
  const zpl = buildBatchLabels(args.labels, options);

  const results: PreviewResult[] = new Array(args.labels.length);

  for (
    let start = 0;
    start < args.labels.length;
    start += PREVIEW_CONCURRENCY
  ) {
    const chunk = args.labels.slice(start, start + PREVIEW_CONCURRENCY);
    const chunkResults = await Promise.all(
      chunk.map((_, offset) =>
        previewZpl(zpl, {
          widthMm: options.widthMm,
          heightMm: options.heightMm,
          dpi: options.dpi,
          index: start + offset
        })
      )
    );
    chunkResults.forEach((r, offset) => {
      results[start + offset] = r;
    });
  }

  const firstError = results.find((r) => !r.ok);
  if (firstError && !firstError.ok) {
    results.forEach((r) => r.ok && r.revoke());
    return { ok: false, error: firstError.error };
  }

  return {
    ok: true,
    items: results
      .map((r, index) =>
        r.ok ? { index, url: r.url, revoke: r.revoke } : null
      )
      .filter((x): x is PreviewItem => x !== null)
  };
}
